import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import instance from "../../api/apiClient";
import { apiRoutes } from "../../configs/apiRoutes";

export type TPermissionKey = 'analytic_casino_games'
  | 'analytic_casino_providers'
  | 'analytic_games_games'
  | 'analytic_games_providers'
  | 'analytic_global_casino'
  | 'analytic_global_common'
  | 'analytic_global_users'
  | 'analytic_notifications_email'
  | 'analytic_notifications_push'
  | 'analytic_notifications_sms'
  | 'analytic_payments_deposits'
  | 'analytic_payments_methods'
  | 'analytic_payments_providers'
  | 'analytic_payments_withdrawals'
  | 'analytic_retention_bonuses'
  | 'analytic_retention_cohorts'
  | 'analytic_retention_players'
  | 'analytic_risk'
  | 'analytic_sports_categories'
  | 'analytic_sports_competitions'
  | 'analytic_support'
  | 'analytic_traffic_affiliates'
  | 'analytic_traffic_cohorts'
  | 'cms_banners'
  | 'cms_customized'
  | 'cms_footer_applications'
  | 'cms_footer_brand_core'
  | 'cms_footer_main_text'
  | 'cms_footer_partners'
  | 'cms_footer_payments'
  | 'cms_footer_social_networks'
  | 'cms_pages_list'
  | 'cms_pages_seo'
  | 'cms_pages_faq'
  | 'cms_promo'
  | 'cms_widgets_burger_menu'
  | 'cms_widgets_casino_page'
  | 'cms_widgets_live_casino'
  | 'cms_widgets_footer'
  | 'cms_widgets_game_bar'
  | 'cms_widgets_header'
  | 'cms_widgets_main_page'
  | 'cms_widgets_mobile_menu'
  | 'cms_widgets_pre_deposit_form'
  | 'cms_widgets_profile_menu'
  | 'cms_widgets_sign_up_form'
  | 'domains_countries'
  | 'domains_mirrors'
  | 'domains_zones'
  | 'game_center_categories'
  | 'game_center_collections'
  | 'game_center_multi_scope'
  | 'game_center_games_active'
  | 'game_center_games_deactivated'
  | 'game_center_games_hidden'
  | 'game_center_games_new'
  | 'game_center_games_no_category'
  | 'game_center_parsing'
  | 'game_center_provider'
  | 'game_center_tags'
  | 'live_ops_bonuses_free_spins'
  | 'live_ops_bonuses_list'
  | 'live_ops_bonuses_loot_boxes'
  | 'live_ops_bonuses_wheels_of_fortune'
  | 'live_ops_cashback_games_list'
  | 'live_ops_cashback_games_not_processing'
  | 'live_ops_cashbacks'
  | 'live_ops_jackpots'
  | 'live_ops_loyalty_program_bets_points'
  | 'live_ops_loyalty_program_deposit_points'
  | 'live_ops_loyalty_program_deposit_points_limits'
  | 'live_ops_loyalty_program_levels'
  | 'live_ops_loyalty_program_sign_up_points'
  | 'live_ops_loyalty_program_wager_points'
  | 'live_ops_missions'
  | 'live_ops_promocodes'
  | 'live_ops_promocodes_bonuses'
  | 'live_ops_store'
  | 'live_ops_tournaments'
  | 'live_ops_wager_games_list'
  | 'live_ops_wager_games_not_processing'
  | 'logs_errors'
  | 'logs_notices'
  | 'logs_warnings'
  | 'notifications_email'
  | 'notifications_email_auto'
  | 'notifications_push_auto'
  | 'notifications_push_manual'
  | 'notifications_sms'
  | 'payments_currencies'
  | 'payments_psp_methods'
  | 'payments_psp_providers'
  | 'payments_transactions_compensations'
  | 'payments_transactions_confiscations'
  | 'payments_transactions_deposits'
  | 'payments_transactions_operations'
  | 'payments_transactions_withdrawals'
  | 'players_list'
  | 'players_presets'
  | 'players_segments'
  | 'players_segments_manual'
  | 'players_segments_auto'
  | 'risk'
  | 'risk_kyc_documents'
  | 'risk_kyc_presets'
  | 'risk_kyc_rules'
  | 'risk_limits'
  | 'risk_permissions'
  | 'risk_players'
  | 'roles_list'
  | 'roles_modules'
  | 'roles_permissions'
  | 'scheduler_list'
  | 'support'
  | 'support_channels'
  | 'support_threads';

export type TPermissionValue = 'create' | 'read' | 'update' | 'delete' | 'export' | 'sensitive_data';
export type TPermissions = {
  [key in TPermissionKey]: Array<TPermissionValue>;
}

type AuthSliceState = {
  isAuthorized?: boolean;
  info?: {
    id: number;
    country: string;
    currency: string;
    email: string;
    firstName: string;
    locale: string;
    publicId: string;
    roleName: string;
    permissions: TPermissions;
    allowedSection1: string[];
    allowedSection2: string[];
    allowedSection3: string[];
  };
};


const initialState: AuthSliceState = {
  isAuthorized: false,
};

export const getUserInfo = createAsyncThunk("user/info", async () => {
  try {
    const result = await instance.get(apiRoutes.me);
    return result.data;
  } catch (error) {
    return undefined;
  }
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setIsAuthorized: (
      state: AuthSliceState,
      action: PayloadAction<boolean>,
    ) => {
      state.isAuthorized = action.payload;
    },
    logout: (state: AuthSliceState) => {
      state.isAuthorized = false;
      state.info = undefined;

      localStorage.removeItem("token");
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      if (!action.payload?.permissions) {
        state.info = undefined;
        return;
      }

      const allowedSection1: string[] = [];
      const allowedSection2: string[] = [];
      const allowedSection3: string[] = [];

      Object.keys(action.payload.permissions).forEach((el) => {
        const result = el.split("_");
        allowedSection1.push(result[0]);
        if (result[1]) allowedSection2.push(result[1]);
        if (result[2]) allowedSection3.push(result[2]);
      });

      state.info = {
        ...action.payload,
        allowedSection1,
        allowedSection2,
        allowedSection3,
      };
    });
  },
});

export const { setIsAuthorized, logout } = authSlice.actions;
